import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BarChart3, CalendarRange, Check, ChevronDown, Download, RotateCw, Sparkles, Star, TrendingDown, TrendingUp } from "lucide-react";
import { useAnalytics, useCampaigns, type AnalyticsOverview, type CampaignSummary } from "@/shared/api/wb";
import { Button, Card, EmptyState, InfoTip, PlatformIcon, type Platform } from "@/shared/ui";
import { fmtCpv, fmtNum, fmtPct, fmtRubFull } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";

type Period = "7d" | "30d" | "all" | "custom";

const fmtDM = (d: Date) =>
  `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;

const toISODate = (d: Date) => d.toISOString().slice(0, 10);

/** Default range when entering custom mode — last 14 days. */
function defaultCustomRange(): { from: string; to: string } {
  const today = new Date();
  const past = new Date(today); past.setDate(today.getDate() - 13);
  return { from: toISODate(past), to: toISODate(today) };
}

/** Compute the current and previous date windows the KPI delta compares. */
function periodRanges(p: Period, custom: { from: string; to: string }): { current: string; previous: string } {
  if (p === "all") {
    return { current: "за всё время", previous: "сравнение недоступно" };
  }
  if (p === "custom") {
    const from = new Date(custom.from);
    const to = new Date(custom.to);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from > to) {
      return { current: "—", previous: "—" };
    }
    const days = Math.max(1, Math.round((+to - +from) / 86400000) + 1);
    const prevEnd = new Date(from); prevEnd.setDate(from.getDate() - 1);
    const prevStart = new Date(prevEnd); prevStart.setDate(prevEnd.getDate() - (days - 1));
    return {
      current: `${fmtDM(from)} – ${fmtDM(to)}`,
      previous: `${fmtDM(prevStart)} – ${fmtDM(prevEnd)}`,
    };
  }
  const days = p === "7d" ? 7 : 30;
  const today = new Date();
  const curStart = new Date(today); curStart.setDate(today.getDate() - days);
  const prevEnd = new Date(curStart);
  const prevStart = new Date(curStart); prevStart.setDate(curStart.getDate() - days);
  return {
    current: `${fmtDM(curStart)} – ${fmtDM(today)}`,
    previous: `${fmtDM(prevStart)} – ${fmtDM(prevEnd)}`,
  };
}

export function AnalyticsPage() {
  // Campaign filter is driven from ?campaign=... query params so the kanban can
  // deep-link to "Аналитика этой РК". Persisted in URL state, not React state,
  // so the user can bookmark / share.
  const { search: locationSearch } = useLocation();
  const navigate = useNavigate();
  const selectedIds = useMemo(() => new URLSearchParams(locationSearch).getAll("campaign"), [locationSearch]);
  const setSelectedIds = (ids: string[]) => {
    const u = new URLSearchParams();
    ids.forEach((id) => u.append("campaign", id));
    navigate({ search: u.toString() }, { replace: true });
  };

  const { data } = useAnalytics(selectedIds);
  const { data: campaigns } = useCampaigns();
  const [period, setPeriod] = useState<Period>("30d");
  const [customRange, setCustomRange] = useState(defaultCustomRange);
  if (!data) return <div className="p-9 text-ink-40">Загрузка…</div>;
  // No campaigns at all → aggregated KPIs would just be zeros and bars would
  // be empty. Show the same welcome surface as other sections instead.
  if ((campaigns?.length ?? 0) === 0) {
    return (
      <div className="px-7 pt-6 pb-14 max-w-[1200px] mx-auto">
        <h1 className="font-display font-extrabold text-[28px] tracking-tight mb-1">Аналитика</h1>
        <EmptyState
          icon={<BarChart3 size={28} strokeWidth={1.8} />}
          title="Аналитика появится после первой РК"
          description={<>
            Запустим хотя бы одну кампанию — и здесь начнём считать охват, клики,
            конверсии и CPV: по каждому каналу и сводно по продукту.
          </>}
          primaryCta={{ label: "Запустить РК", to: "/campaign/new" }}
          secondaryLink={{ label: "К списку РК", to: "/account/projects" }}
        />
      </div>
    );
  }
  const ranges = periodRanges(period, customRange);
  const scope = selectedIds.length === 0
    ? "Сводка по всем РК"
    : selectedIds.length === 1
      ? `РК · ${(campaigns ?? []).find((c) => c.id === selectedIds[0])?.product ?? "выбранная"}`
      : `Сводка по ${selectedIds.length} РК`;

  return (
    <div className="px-7 pt-6 pb-14 max-w-[1200px] mx-auto">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40">{scope}</span>
            <ProBadge />
          </div>
          <h1 className="font-display font-extrabold text-[28px] tracking-tight">Аналитика</h1>
        </div>
        <div className="flex items-center gap-2.5">
          <CampaignFilterDropdown campaigns={campaigns ?? []} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
          <PeriodSelector
            value={period} onChange={setPeriod}
            custom={customRange} onCustomChange={setCustomRange}
          />
          <Button variant="outline" size="sm"><Download size={14} /> Экспорт</Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-5">
        {data.kpis.map((k) => <KpiCard key={k.key} k={k} ranges={ranges} />)}
      </div>

      <div className="grid grid-cols-[1fr_1fr] gap-4 mb-5">
        <ReachByCampaign bars={data.bars} />
        <SocialBreakdown socials={data.socials} insight={data.social_insight} />
      </div>

      <BestChannels rows={data.best_channels} />
    </div>
  );
}

/**
 * Period picker that combines the three preset windows with a "custom" pill
 * that opens a from/to popover. Apply commits the custom range; Cancel
 * reverts. Closes on outside click and on Apply/Cancel.
 */
function PeriodSelector({
  value, onChange, custom, onCustomChange,
}: {
  value: Period; onChange: (p: Period) => void;
  custom: { from: string; to: string }; onCustomChange: (c: { from: string; to: string }) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(custom.from);
  const [draftTo, setDraftTo] = useState(custom.to);

  const openPicker = () => {
    setDraftFrom(custom.from);
    setDraftTo(custom.to);
    setPickerOpen(true);
  };
  const apply = () => {
    if (draftFrom && draftTo && draftFrom <= draftTo) {
      onCustomChange({ from: draftFrom, to: draftTo });
      onChange("custom");
    }
    setPickerOpen(false);
  };

  const customLabel = value === "custom"
    ? `${fmtDM(new Date(custom.from))} – ${fmtDM(new Date(custom.to))}`
    : "Свой период";

  // Render the presets + custom pill as one segmented group so exactly one is
  // highlighted at a time. Clicking the custom pill toggles the popover; if
  // already active, it just re-opens the picker.
  return (
    <div className="relative inline-flex items-center p-1 bg-ink-6 rounded-pill gap-1">
      {(["7d", "30d", "all"] as const).map((v) => {
        const label = v === "7d" ? "7 дней" : v === "30d" ? "30 дней" : "Всё время";
        const active = value === v;
        return (
          <button key={v} type="button" onClick={() => onChange(v)}
            className={cn("inline-flex items-center px-3 py-1 rounded-pill text-[13px] font-display font-semibold transition-colors",
              active ? "bg-white text-ink shadow-sm" : "text-ink-60 hover:text-ink")}>
            {label}
          </button>
        );
      })}
      <button type="button" onClick={openPicker}
        className={cn("inline-flex items-center gap-1.5 px-3 py-1 rounded-pill text-[13px] font-display font-semibold transition-colors",
          value === "custom" ? "bg-white text-ink shadow-sm" : "text-ink-60 hover:text-ink")}>
        <CalendarRange size={13} /> {customLabel}
      </button>

      {pickerOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setPickerOpen(false)} />
          <div className="absolute top-[calc(100%+6px)] right-0 z-40 w-[300px] rounded-md bg-white border border-ink-12 shadow-md p-4 fade-up grid gap-3">
            <div className="grid grid-cols-2 gap-2.5">
              <label className="grid gap-1">
                <span className="text-[10.5px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">С</span>
                <input type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)}
                  className="h-9 px-2.5 rounded-sm border border-ink-12 bg-white text-[13px] font-mono outline-none focus:border-ink" />
              </label>
              <label className="grid gap-1">
                <span className="text-[10.5px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">По</span>
                <input type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} max={toISODate(new Date())}
                  className="h-9 px-2.5 rounded-sm border border-ink-12 bg-white text-[13px] font-mono outline-none focus:border-ink" />
              </label>
            </div>
            {draftFrom && draftTo && draftFrom > draftTo && (
              <div className="text-[11.5px] text-danger">«С» должно быть не позже «По»</div>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => setPickerOpen(false)}>Отмена</Button>
              <Button size="sm" onClick={apply} disabled={!draftFrom || !draftTo || draftFrom > draftTo}>
                Применить
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ProBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-pill bg-gradient-to-r from-purple-900 to-pink text-white text-[10px] font-display font-bold uppercase tracking-wider">
      <Sparkles size={10} /> PRO
    </span>
  );
}

function CampaignFilterDropdown({ campaigns, selectedIds, setSelectedIds }: {
  campaigns: CampaignSummary[]; selectedIds: string[]; setSelectedIds: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const buttonLabel = selectedIds.length === 0
    ? "Все РК"
    : selectedIds.length === 1
      ? campaigns.find((c) => c.id === selectedIds[0])?.product ?? "1 РК"
      : `Выбрано ${selectedIds.length}`;
  const toggle = (id: string) => {
    setSelectedIds(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-pill border-2 border-ink-12 bg-white text-[13px] font-display font-semibold text-ink hover:border-ink/40">
        {buttonLabel}
        {selectedIds.length > 0 && (
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-ink text-white text-[10px] font-mono">{selectedIds.length}</span>
        )}
        <ChevronDown size={14} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute top-[calc(100%+6px)] right-0 z-40 w-[300px] max-h-[420px] overflow-y-auto rounded-md bg-white border border-ink-12 shadow-md fade-up">
            <button onClick={() => { setSelectedIds([]); setOpen(false); }}
              className="w-full text-left px-3.5 py-2.5 text-[13px] font-display font-semibold border-b border-ink-12 hover:bg-ink-6">
              Все РК {selectedIds.length === 0 && <Check size={13} className="inline ml-1 text-success" strokeWidth={3} />}
            </button>
            {campaigns.map((c) => (
              <button key={c.id} onClick={() => toggle(c.id)}
                className="w-full text-left px-3.5 py-2 flex items-center gap-2 hover:bg-ink-6">
                <div className={cn("w-4 h-4 rounded-sm border-2 flex-shrink-0 grid place-items-center",
                  selectedIds.includes(c.id) ? "bg-ink border-ink" : "border-ink-12")}>
                  {selectedIds.includes(c.id) && <Check size={10} strokeWidth={3} className="text-white" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-display font-semibold truncate">{c.product}</div>
                  <div className="text-[11px] text-ink-40">{c.type === "seeding" ? "Посев" : "Натив"} · {c.status}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ k, ranges }: { k: AnalyticsOverview["kpis"][number]; ranges: { current: string; previous: string } }) {
  const goodLow = k.good_is === "low";
  const positive = goodLow ? k.delta_pct < 0 : k.delta_pct > 0;
  const fmt = k.format === "rub" ? fmtRubFull(k.value) : k.format === "cpv" ? fmtCpv(k.value) : fmtNum(k.value);
  return (
    <Card className="!p-4">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-display font-semibold text-ink-40">
        {k.label} <InfoTip align="left">{k.tip}</InfoTip>
      </div>
      <div className="font-display font-extrabold text-[26px] mt-1 tabular-nums">{fmt}</div>
      <div className={cn("text-[12px] mt-1 inline-flex items-center gap-1 font-semibold",
        k.good_is === "neutral" ? "text-ink-60" : positive ? "text-success" : "text-danger")}>
        {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
        {k.delta_pct > 0 ? "+" : ""}{fmtPct(k.delta_pct)}
        <PeriodCompareLabel ranges={ranges} />
      </div>
    </Card>
  );
}

function PeriodCompareLabel({ ranges }: { ranges: { current: string; previous: string } }) {
  return (
    <span className="relative group">
      <span className="underline decoration-dotted decoration-ink-40 underline-offset-2 cursor-help">к прошлому периоду</span>
      <span className="hidden group-hover:block absolute left-1/2 -translate-x-1/2 top-[calc(100%+6px)] z-50 w-[200px] px-3 py-2 rounded-md bg-ink text-white text-[11.5px] leading-snug shadow-md pointer-events-none normal-case tracking-normal font-normal">
        <div className="flex justify-between gap-2 mb-1">
          <span className="text-white/60">Сейчас</span>
          <span className="font-mono">{ranges.current}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-white/60">Прошлый</span>
          <span className="font-mono">{ranges.previous}</span>
        </div>
      </span>
    </span>
  );
}

function ReachByCampaign({ bars }: { bars: AnalyticsOverview["bars"] }) {
  const max = Math.max(...bars.map((b) => b.value), 1);
  return (
    <Card className="!p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold text-[16px]">Охват по кампаниям</div>
        <div className="text-[12px] text-ink-40">уник. пользователей</div>
      </div>
      <div className="flex items-end gap-3 h-[200px] pt-4">
        {bars.map((b, i) => {
          const h = Math.max(6, (b.value / max) * 150);
          const winner = i === 0;
          return (
            <div key={b.id} className="flex-1 flex flex-col items-center min-w-0">
              <div className="text-[11px] font-mono font-bold mb-1.5 tabular-nums">{fmtNum(b.value)}</div>
              <div className={cn("w-full rounded-t-md transition-[height]",
                winner ? "bg-lime" : b.type === "seeding" ? "bg-ink/85" : "bg-pink")}
                style={{ height: `${h}px` }} />
              <div className="text-[11px] font-display font-semibold text-ink-60 text-center mt-2 truncate max-w-full px-0.5">{b.label}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function SocialBreakdown({ socials, insight }: { socials: AnalyticsOverview["socials"]; insight: string | null }) {
  return (
    <Card className="!p-5">
      <div className="font-display font-bold text-[16px] mb-4">Соцсети</div>
      <div className="grid gap-3.5">
        {socials.map((s) => (
          <div key={s.platform}>
            <div className="flex items-center gap-2 mb-1.5">
              <PlatformIcon platform={s.platform as Platform} size={15} />
              <span className="text-[13px] font-semibold flex-1 capitalize">{s.platform}</span>
              <span className="font-mono text-[12px] text-ink-40">CPV {s.cpv ? `${s.cpv}₽` : "—"}</span>
              <span className="font-mono text-[13px] font-bold tabular-nums w-[42px] text-right">{s.share_pct}%</span>
            </div>
            <div className="h-1.5 rounded-pill bg-ink-6 overflow-hidden">
              <div className="h-full bg-lime rounded-pill transition-[width]" style={{ width: `${s.share_pct}%` }} />
            </div>
          </div>
        ))}
      </div>
      {insight && (
        <div className="mt-4 p-3 rounded-md bg-cream text-[12.5px] text-ink-60 leading-relaxed">
          <b>Инсайт. </b>{insight}
        </div>
      )}
    </Card>
  );
}

function BestChannels({ rows }: { rows: AnalyticsOverview["best_channels"] }) {
  return (
    <Card className="!p-5">
      <div className="flex items-center gap-2 mb-3">
        <Star size={16} className="text-lime" fill="var(--wb-lime)" />
        <div className="font-display font-bold text-[16px]">Лучшие каналы</div>
        <InfoTip align="left">Закрытые размещения, отсортированные по CPV. Кандидаты на повтор и масштабирование.</InfoTip>
      </div>
      <div className="grid gap-1.5">
        {rows.map((r, i) => (
          <div key={r.blogger_id} className="grid grid-cols-[34px_2fr_64px_64px_64px_64px_auto] gap-3 items-center py-2 px-2 rounded-md hover:bg-ink-6">
            <div className="w-[34px] h-[34px] rounded-full grid place-items-center font-display font-bold text-white text-[12px]"
              style={{ background: "linear-gradient(135deg, #BFF93F, #7BD000)" }}>
              {r.name.slice(1, 3).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 font-display font-semibold text-[13.5px]">
                <PlatformIcon platform={r.platform as Platform} size={12} /> {r.name}
                {i === 0 && (
                  <span className="text-[9px] font-mono font-bold bg-lime text-[var(--fg-on-accent)] px-1.5 py-0.5 rounded-sm">★ ТОП</span>
                )}
              </div>
              <div className="text-[11.5px] text-ink-40">тематика</div>
            </div>
            <Cell l="Просмотры" v={fmtNum(r.views)} />
            <Cell l="CTR"       v="—" />
            <Cell l="CPV"       v={`${r.cpv}₽`} accent={r.cpv < 0.5} />
            <Cell l="Конв."     v={String(r.conv)} />
            <Button size="sm" variant="outline"><RotateCw size={12} /> Повторить</Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Cell({ l, v, accent }: { l: string; v: string; accent?: boolean }) {
  return (
    <div className="text-right">
      <div className={cn("font-mono font-bold text-[13.5px]", accent ? "text-success" : "text-ink")}>{v}</div>
      <div className="text-[10.5px] uppercase tracking-wider text-ink-40">{l}</div>
    </div>
  );
}
